import React from 'react';
import { withRouter } from 'react-router-dom';
import { Avatar, Button, Card, CardContent, Typography, Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';

import CampaignLogo from '../../../assets/campaign-logo.svg';
import { formatCount, formatINR, formatDate, titleCase } from '../../../utils/formatters';

const ACCENT = '#111936';

const STATUS_STYLE = {
  live: { label: 'Live', color: '#7C3AED', background: '#F3E8FF', dot: true },
  running: { label: 'Running', color: '#7C3AED', background: '#F3E8FF', dot: true },
  completed: { label: 'Completed', color: '#15803D', background: '#DCFCE7' },
  paused: { label: 'Paused', color: '#92400E', background: '#FEF3C7' },
  draft: { label: 'Draft', color: '#4B5563', background: '#F3F4F6' },
};

const styles = () => ({
  card: {
    marginBottom: 14,
    borderRadius: 12,
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
    cursor: 'pointer',
    '&:active': { background: '#FAFAFA' },
  },
  name: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 14.5,
    lineHeight: '20px',
    color: '#111827',
  },
  subtitle: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 12,
    lineHeight: '16px',
    color: '#6B7280',
    marginTop: 2,
  },
  description: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 12.5,
    lineHeight: '19px',
    color: '#4B5563',
    marginTop: 10,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    borderRadius: 999,
    padding: '3px 10px',
    fontFamily: "'Lato', sans-serif",
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  '@keyframes livePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0.45)' },
    '70%': { boxShadow: '0 0 0 5px rgba(124, 58, 237, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0)' },
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#7C3AED',
    animation: '$livePulse 1.6s ease-out infinite',
  },
  statsRow: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #F3F4F6',
  },
  statCell: {
    paddingBottom: 8,
  },
  statValue: {
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 14,
    color: '#111827',
    lineHeight: '18px',
  },
  statLabel: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 10.5,
    color: '#9CA3AF',
    marginTop: 1,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  footer: {
    marginTop: 4,
    paddingTop: 10,
    borderTop: '1px solid #F3F4F6',
  },
  dateText: {
    fontFamily: "'Lato', sans-serif",
    fontSize: 11.5,
    color: '#6B7280',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  brandAvatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: '1px solid #E5E7EB',
    background: '#F9FAFB',
  },
  invoiceButton: {
    textTransform: 'none',
    fontFamily: "'Lato', sans-serif",
    fontWeight: 700,
    fontSize: 13,
    color: 'white',
    background: ACCENT,
    borderRadius: 8,
    padding: '7px 0',
    marginTop: 12,
    width: '100%',
    '&:hover': { background: '#177E91' },
  },
});

function Stat({ classes, value, label, hidden }) {
  return (
    <Grid item xs={6} className={classes.statCell}>
      <Typography className={classes.statValue}>{hidden ? '-' : value}</Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </Grid>
  );
}

class MobileCampaign extends React.Component {
  state = { logoBroken: false };

  openFeed = () => {
    this.props.history.push(
      `${this.props.match.path}/${this.props.id}/feeds/${
        JSON.parse(localStorage.getItem('user')).influencer.id
      }/youtube/`,
    );
  };

  render() {
    const { classes } = this.props;
    const statusKey = this.props.is_live ? 'live' : this.props.status;
    const status = STATUS_STYLE[statusKey] || {
      label: titleCase(this.props.status),
      color: '#4B5563',
      background: '#F3F4F6',
    };
    const started = this.props.is_live || this.props.status !== 'draft';
    const brandName = this.props.brand_name;

    return (
      <Card elevation={0} className={classes.card} onClick={this.openFeed}>
        <CardContent style={{ padding: '14px 16px' }}>
          <Grid container justifyContent="space-between" alignItems="flex-start" spacing={1} wrap="nowrap">
            <Grid item xs>
              <Grid container spacing={1} alignItems="center" wrap="nowrap">
                <Grid item>
                  <Avatar
                    variant="rounded"
                    src={
                      this.state.logoBroken || !this.props.brand_logo_url
                        ? CampaignLogo
                        : this.props.brand_logo_url
                    }
                    imgProps={{
                      style: { objectFit: 'contain' },
                      // A logo URL we hold but S3 no longer serves used to
                      // render as an empty box; fall back to the placeholder.
                      onError: () => this.setState({ logoBroken: true }),
                    }}
                    alt={brandName || 'brand logo'}
                    className={classes.brandAvatar}
                  />
                </Grid>
                <Grid item xs>
                  <Typography className={`${classes.name} campaign-name`}>
                    {this.props.name}
                  </Typography>
                  <Typography className={`${classes.subtitle} campaign-objective`}>
                    {brandName ? `${brandName}` : ''}
                    {brandName && this.props.objective ? ' · ' : ''}
                    {this.props.objective || ''}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <span
                className={classes.statusChip}
                style={{ color: status.color, background: status.background }}
              >
                {status.dot ? <span className={classes.liveDot} /> : null}
                {status.label}
              </span>
            </Grid>
          </Grid>

          {this.props.desc ? (
            <Typography className={`${classes.description} campaign-description`}>
              {this.props.desc}
            </Typography>
          ) : null}

          <Grid container className={classes.statsRow}>
            <Stat classes={classes} value={formatINR(this.props.budget)} label="Your payout" />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_posts)}
              label="Posts"
              hidden={!started}
            />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_views)}
              label="Views"
              hidden={!started}
            />
            <Stat
              classes={classes}
              value={formatCount(this.props.total_engagement_rate)}
              label="Engagement"
              hidden={!started}
            />
          </Grid>

          <Grid container className={classes.footer}>
            <Typography component="span" className={classes.dateText}>
              <CalendarTodayOutlinedIcon style={{ fontSize: 13 }} />
              {formatDate(this.props.start_date)} to {formatDate(this.props.end_date)}
              {this.props.duration ? ` · ${this.props.duration} days` : ''}
            </Typography>
          </Grid>

          {this.props.can_raise_invoice ? (
            <Button
              size="small"
              className={classes.invoiceButton}
              onClick={(event) => {
                // The whole card is a link to the campaign feed.
                event.stopPropagation();
                this.props.history.push('/influencer/invoices');
              }}
            >
              Raise invoice
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }
}

export default withRouter(withStyles(styles)(MobileCampaign));
